/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, August 22nd 2022, 12:40:09 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import CancelIcon from "@mui/icons-material/Cancel";
import { ConditionSettings } from "common/components/conditions";
import { ActivationCondition, FormConfiguration, FormField } from "lib/schemas";
import { useActivationConditionAPIForm } from "service-builder/services/OutgoingBranchesConfigurationAPI";
import { OSDialogCloseButton, OSLoadingButton, RightAlignContainer, OSAlert } from "ui-widgets";

interface ActivationConditionFormProps {
  activationCondition: ActivationCondition;
  updateActivationConditionFromAPICallback: (savedCondition: ActivationCondition) => void;
  onClose: () => void;
}

export const ActivationConditionForm = (props: ActivationConditionFormProps) => {
  const {
    isSuccessfulAPIDataSend,
    isAPIDataSendError,
    apiDataSendErrors,
    watchedConditionSettings,
    isSendingDataToAPI,
    submitAPIForm,
    onChangeConditionForm,
    onChangeConditionField,
    onChangeConditionOperator,
    onChangeConditionValue,
  } = useActivationConditionFormLogic(props);

  return (
    <Paper sx={{ paddingRight: 4, paddingLeft: 4, paddingBottom: 4 }} elevation={10}>
      <Stack spacing={2}>
        <OSDialogCloseButton
          closeIconButtonComponent={<CancelIcon color="primary" />}
          sx={{ marginTop: 1, marginLeft: "95%" }}
          onClick={(e) => props.onClose()}
        />

        {isSuccessfulAPIDataSend && (
          <OSAlert variant="standard" severity="success">
            Condition updated
          </OSAlert>
        )}

        {isAPIDataSendError && (
          <OSAlert variant="standard" severity="error">
            Could not save Condition. {apiDataSendErrors.detail}
          </OSAlert>
        )}
      </Stack>
      <ConditionSettings
        conditionSettings={watchedConditionSettings}
        onChangeConditionForm={onChangeConditionForm}
        onChangeConditionField={onChangeConditionField}
        onChangeConditionOperator={onChangeConditionOperator}
        onChangeConditionValue={onChangeConditionValue}
      />

      <RightAlignContainer>
        <OSLoadingButton variant="contained" isLoading={isSendingDataToAPI} onClick={submitAPIForm}>
          Save Condition
        </OSLoadingButton>
      </RightAlignContainer>
    </Paper>
  );
};

const useActivationConditionFormLogic = ({
  activationCondition,
  updateActivationConditionFromAPICallback,
}: ActivationConditionFormProps) => {
  const useAPIFormReturn = useActivationConditionAPIForm({
    activationConditionFetchId: null,
    activationConditionDefaultValues: activationCondition,
    updateActivationConditionFromAPICallback,
  });

  const {
    useFormReturn: { watch, setValue },
    submitAPIForm,
    isSuccessfulAPIDataSend,
    isAPIDataSendError,
    isSendingDataToAPI,
    apiDataSendErrors,
  } = useAPIFormReturn;

  const watchedConditionSettings = {
    condition_form: watch("condition_form"),
    condition_field: watch("condition_field"),
    condition_operator: watch("condition_operator"),
    condition_value: watch("condition_value"),
  };

  return {
    isSuccessfulAPIDataSend,
    isAPIDataSendError,
    apiDataSendErrors,
    watchedConditionSettings,
    isSendingDataToAPI,
    submitAPIForm,
    onChangeConditionForm: (form: FormConfiguration) => setValue("condition_form", form.id),
    onChangeConditionField: (field: FormField) => setValue("condition_field", field?.id ?? null),
    onChangeConditionOperator: (condition_operator: ActivationCondition["condition_operator"]) =>
      setValue("condition_operator", condition_operator),
    onChangeConditionValue: (condition_value: ActivationCondition["condition_value"]) =>
      setValue("condition_value", condition_value),
  };
};
